<template>
  <div
    class="playlist-layer playlist-item multipost"
    v-if="theme"
    :data-test="`theme:${theme._id}`"
    :class="[{'empty' : !item.posts.length}, {'editing': isEditing}]"
    @click="onClickTimelineItem"
  >
    <div class="layer-info">
      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="fas fa-grip-vertical"></span>
      </button>

      <div class="layer-title flex-grow ml-2">
        <div class="flex-grow ellipsis">
          <div class="title-text">{{item.title}}</div>
          <div class="description-text capitalize">{{theme.layout}}</div>
        </div>
      </div>

      <div class="layer-description ml-1 capitalize">
        <duration v-model:duration="item.displayTime"></duration>
      </div>

      <preview-live-switch :item='item'></preview-live-switch>

      <dropdown position="left" class="more-actions ml-1">
        <template #trigger>
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <span class="fas fa-ellipsis-v"></span>
          </button>
        </template>

        <template #dropdown-body>
          <button
            class="dropdown-list-item"
            v-on:click="duplicateItem"
          >
            <span class="dropdown-list-item-label">
              Duplicate in playlist
            </span>
          </button>
          <button
            v-if="canDuplicateInGroup"
            class="dropdown-list-item"
            v-on:click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Group
            </span>
          </button>
          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>
          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>
          <button v-if="posts.length" class="dropdown-list-item" @click="clearPosts">
            <span class="dropdown-list-item-label warning">Clear Posts</span>
          </button>
          <button
            class="dropdown-list-item"
            v-on:click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>

    <div class="layer-rundown">
      <draggable
        ref="tgbDropArea"
        class="layer-draggable tgbDropArea tgbTagboardDrop draggable"
        v-model="posts"
        v-bind="{ group: { name: 'tagboard'}, animation: 200 }"
        @start="onStart"
        :move="checkMove"
        @unchoose="onUnchoose"
        revert-on-spill="true"
        data-test="producer-socialBoard-dropzone"
      >
        <template v-if="item.posts.length">
          <div
            v-if="tagboard"
            class="layer-tagboard"
            :data-test="`tagboard:${tagboard._id}`"
          >
            <span
              v-if="hasAvatar"
              class="tagboard-avatar"
              :style="{ 'background-image' : 'url(' + hasAvatar + ')' }"
            />

            <div class="tagboard-title">{{tagboard.title}}</div>
            <div class="tagboard-description ml-1">{{tagboard.description}}</div>

            <dropdown position="left" class="more-actions ml-1">
              <template #trigger>
                <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
                  <span class="fas fa-ellipsis-v"></span>
                </button>
              </template>

              <template #dropdown-body>
                <div
                  class="dropdown-list-item"
                  @click="clearPosts"
                >
                  <div class="dropdown-list-item-label warning">
                    Remove
                  </div>
                </div>
              </template>
            </dropdown>
          </div>

          <div
            v-else class="layer-tagboard deleted"
            :class="[{'active' : (isCurrentPost || isCurrentSamplePost) }]"
          >
            <div class="deleted-message">This Board has been deleted.</div>

            <button
              class="tb-btn tb-btn-text sm medium-em ml-1"
              title="Remove item from playlist"
              @click.stop.prevent="clearPosts"
            >
              <i class="fas fa-minus-circle"></i>
            </button>
          </div>
        </template>

        <!-- Dropzone Empty State -->
        <div class="tgbDropAreaHelp" v-if="!item.posts.length">
          <p>Drag and drop a Board here</p>
        </div>
      </draggable>
    </div>
  </div>

  <div
    v-else class="playlist-layer playlist-item deleted"
    :class="{ active: (isCurrentLayout || isCurrentSampleLayout) }"
  >
    <div class="layer-info">
      <div class="deleted-message">
        This Theme <span v-if="item.title">({{item.title}})</span> has been deleted.
      </div>
      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        v-on:click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>
</template>

<script>
import DurationComponent from '../duration-input/DurationInput.vue';
import TimelineMixins from '../../../mixins/timeline-mixins';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';

export default {
  name: 'TimelineMultipostComponent',
  props: ['item'],
  mixins: [TimelineMixins],
  components: {
    duration: DurationComponent,
    'preview-live-switch': PreviewLiveSwitch,
  },
  methods: {
    clearPosts() {
      this.posts = [];
    },

    checkMove(e) {
      const { type } = e.draggedContext.element;
      const hasContent = Boolean(e.relatedContext.list.length);
      const notAllowed = (hasContent && type === 'tagboard');

      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';
        this.error = 'A full Board cannot be presented with other content.';
        return false;
      }

      this.error = false;
      return true;
    },

    onStart() {
      const dropzones = document.querySelectorAll('.tgbTagboardDrop,.tgbPostDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    onUnchoose(e) {
      e.item.style.cursor = 'pointer';

      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },
  },
  watch: {
    posts() {
      if (this.isCurrentLayout && !this.isCurrentPost && this.item.posts.length) {
        // Make sure tagboard is automatically made live whenever layout goes live
        const [tagboard] = this.item.posts;

        this.$store.dispatch('toggleLivePost', {
          parentTimelineId: this.item.timeline_id,
          item: tagboard,
          eventSource: 'tagboard',
        });
      }
    },
  },
  computed: {
    posts: {
      get() {
        return this.item.posts;
      },

      set(posts) {
        this.$store.dispatch('setPosts', {
          posts,
          timelineId: this.item.timeline_id,
        });
      },
    },

    isValid() {
      return this.tagboard && this.item.posts.length && this.item.posts[0].timeline_id;
    },

    isCurrentPost() {
      return (
        this.item.posts.length
        && this.item.timeline_id === this.currentLayoutIndex
        && this.currentPostIndex === this.item.posts[0].timeline_id
      );
    },

    hasAvatar() {
      return this.tagboard.avatar_url ? this.tagboard.avatar_url : 'https://static.tagboard.com/producer/assets/img/logo-hash-t-white.svg';
    },

    getCoverUrl() {
      return this.tagboard.cover_url ? this.tagboard.cover_url : '';
    },

    production() {
      return this.$store.state.production.production;
    },

    tagboard() {
      const { posts } = this.item;
      if (!posts.length) { return {}; }

      // We should probably KNOW which ID field to use...
      const [post] = posts;
      if (!post) { return {}; }

      const postId = posts[0]._id || posts[0].id;
      if (!postId) { return {}; }

      const { lookupTable } = this.$store.state.tagboards;
      return lookupTable[postId];
    },

    theme() {
      return this.$store.state.themes.lookupTable[this.item._id];
    },
  },
};
</script>
